<template>
  <div class="validators-view">
    <h1 class="text-h4 mb-6">{{ t('validators.title') }}</h1>
    
    <!-- Estatísticas dos Validadores -->
    <v-row>
      <v-col cols="12" sm="6" md="3">
        <StatusCard
          titulo="Validadores Ativos"
          :valor="blockchainStore.getActiveValidatorsCount"
          :subtitulo="`de ${blockchainStore.validators.length} total`"
          icone="mdi-check-network-outline"
          background="primary"
        />
      </v-col>
      
      <v-col cols="12" sm="6" md="3">
        <StatusCard
          titulo="Stake Total"
          :valor="formatarValorToken(blockchainStore.getTotalStake)"
          :subtitulo="`${coin} em stake`"
          icone="mdi-currency-usd"
        />
      </v-col>
      
      <v-col cols="12" sm="6" md="3">
        <StatusCard
          titulo="Mérito Médio"
          :valor="`${calcularMeritoMedio()}/100`"
          subtitulo="Pontuação média"
          icone="mdi-medal-outline"
        />
      </v-col>
      
      <v-col cols="12" sm="6" md="3">
        <StatusCard
          titulo="Uptime da Rede"
          :valor="`${calcularUptimeMedio().toFixed(2)}%`"
          subtitulo="Última época"
          icone="mdi-arrow-up-bold-circle-outline"
        />
      </v-col>
    </v-row>
    
    <!-- Visualização do Consenso PoSEM -->
    <v-card class="my-6">
      <v-card-title>
        <v-icon color="primary" class="mr-2">mdi-chart-bubble</v-icon>
        Visualização do Consenso PoSEM
      </v-card-title>
      
      <v-divider></v-divider>
      
      <v-card-text>
        <div class="text-center mb-4">
          <p class="text-body-1">
            Visualização da distribuição de validadores baseada em Stake e Mérito no Ecossistema.
            <br>O tamanho de cada nó representa o stake, a cor representa o mérito, e a posição mostra a probabilidade de ser selecionado como líder.
          </p>
          
          <div class="d-flex justify-space-between align-center mb-2 px-4">
            <span class="text-caption">{{ t('validators.lowerProb') }}</span>
            <span class="text-caption">{{ t('validators.higherProb') }}</span>
          </div>
        </div>
        
        <!-- Visual simplificado do consenso (em produção, seria uma visualização 3D interativa) -->
        <div class="posem-visualization mb-4">
          <div 
            v-for="(validator, index) in blockchainStore.validators" 
            :key="validator.address"
            class="validator-node"
            :style="{
              left: `${20 + (index * 15)}%`,
              top: `${30 + Math.sin(index) * 20}%`,
              width: `${30 + (validator.stake / blockchainStore.getTotalStake) * 100}px`,
              height: `${30 + (validator.stake / blockchainStore.getTotalStake) * 100}px`,
              backgroundColor: getValidatorColor(validator.merit),
              opacity: validator.status === 'active' ? 1 : 0.5
            }"
            @mouseover="showValidatorInfo = validator"
            @mouseleave="showValidatorInfo = null"
          >
            <span class="validator-initial">{{ validator.name.charAt(0) }}</span>
            
            <div v-if="showValidatorInfo === validator" class="validator-tooltip">
              <div class="font-weight-medium">{{ validator.name }}</div>
              <div class="text-caption">Stake: {{ formatarValorToken(validator.stake) }}</div>
              <div class="text-caption">Mérito: {{ validator.merit }}/100</div>
              <div class="text-caption">Probabilidade: {{ ((validator.stake * (1 + 0.1 * validator.merit)) / getTotalWeightedStake() * 100).toFixed(1) }}%</div>
            </div>
          </div>
        </div>
        
        <div class="text-center">
          <v-btn
            color="primary"
            variant="outlined"
            prepend-icon="mdi-rotate-3d"
            @click="abrirVisualizacao3D"
            class="mt-4"
          >
            Abrir Visualização 3D Completa
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
    
    <!-- Tabela de Validadores -->
    <v-card>
      <v-card-title class="d-flex justify-space-between">
        <div>
          <v-icon color="primary" class="mr-2">mdi-view-list</v-icon>
          Lista de Validadores
        </div>
        
        <v-text-field
          v-model="busca"
          append-icon="mdi-magnify"
          label="Buscar validador"
          single-line
          hide-details
          density="compact"
          style="max-width: 300px;"
        ></v-text-field>
      </v-card-title>
      
      <v-divider></v-divider>
      
      <!-- Filtros geográficos e toggle de agrupamento -->
      <v-card-text class="pb-0">
        <v-row align="center">
          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="filtroCountry"
              :items="paisesDisponiveis"
              label="Filtrar por país"
              density="compact"
              variant="outlined"
              clearable
              hide-details
              prepend-inner-icon="mdi-earth"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="filtroRegion"
              :items="regioesDisponiveis"
              label="Filtrar por região"
              density="compact"
              variant="outlined"
              clearable
              hide-details
              :disabled="!filtroCountry"
              prepend-inner-icon="mdi-map-marker"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-switch
              v-model="modoAgrupado"
              label="Agrupar por país"
              density="compact"
              hide-details
              color="primary"
            ></v-switch>
          </v-col>
        </v-row>
      </v-card-text>
      
      <v-divider class="mt-2"></v-divider>
      
      <!-- Visualização agrupada por país -->
      <template v-if="modoAgrupado">
        <div v-if="validadoresAgrupados.length === 0" class="pa-8 text-center">
          <v-icon size="48" color="grey" class="mb-2">mdi-account-search-outline</v-icon>
          <div class="text-body-1">{{ t('validators.noValidators') }}</div>
        </div>
        <v-expansion-panels v-else variant="accordion">
          <v-expansion-panel
            v-for="grupo in validadoresAgrupados"
            :key="grupo.country"
          >
            <v-expansion-panel-title>
              <v-icon class="mr-2" color="primary">mdi-earth</v-icon>
              {{ grupo.country }} ({{ grupo.count }} {{ grupo.count === 1 ? 'validador' : 'validadores' }})
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-data-table
                :headers="cabecalhosValidadores"
                :items="grupo.validators"
                :loading="blockchainStore.isLoadingValidators"
                loading-text="Carregando validadores..."
                no-data-text="Nenhum validador encontrado"
                hover
                item-value="address"
                density="compact"
              >
                <template v-slot:item.status="{ item }">
                  <v-chip :color="item.status === 'active' ? 'success' : 'grey'" size="small" label>
                    {{ item.status === 'active' ? 'Ativo' : 'Inativo' }}
                  </v-chip>
                </template>
                <template v-slot:item.name="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar :color="getValidatorColor(item.merit)" size="32" class="mr-2 text-white">
                      {{ item.name.charAt(0) }}
                    </v-avatar>
                    <div>
                      <router-link :to="{ name: 'validator-details', params: { id: item.address } }" class="text-decoration-none font-weight-medium">
                        {{ item.name }}
                      </router-link>
                      <div class="text-caption text-truncate" style="max-width: 180px;">{{ formatarHash(item.address) }}</div>
                    </div>
                  </div>
                </template>
                <template v-slot:item.stake="{ item }">
                  <div class="font-weight-medium">{{ formatarValorToken(item.stake) }}</div>
                </template>
                <template v-slot:item.merit="{ item }">
                  <div class="text-caption text-center">{{ item.merit }}/100</div>
                </template>
                <template v-slot:item.signatureScheme="{ item }">
                  <v-chip :color="item.signatureScheme === 'Dilithium3' ? 'purple' : 'blue-grey'" size="small" label>{{ item.signatureScheme }}</v-chip>
                </template>
                <template v-slot:item.posemScore="{ item }">
                  <div class="text-caption text-center">{{ item.posemScore }}/100</div>
                </template>
                <template v-slot:item.probability="{ item }">
                  <div class="text-caption">{{ ((item.stake * (1 + 0.1 * item.merit)) / getTotalWeightedStake() * 100).toFixed(1) }}%</div>
                </template>
                <template v-slot:item.blocksProposed="{ item }">
                  <div class="text-caption">{{ formatarNumeroGrande(item.blocksProposed) }}</div>
                </template>
                <template v-slot:item.uptime="{ item }">
                  <div class="text-caption text-center">{{ item.uptime.toFixed(2) }}%</div>
                </template>
                <template v-slot:item.lastActive="{ item }">
                  <div class="text-caption">{{ tempoRelativo(item.lastActive) }}</div>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn icon variant="text" density="comfortable" size="small" :to="{ name: 'validator-details', params: { id: item.address } }">
                    <v-icon>mdi-information-outline</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
      
      <!-- Visualização em lista plana (padrão) -->
      <v-data-table
        v-else
        :headers="cabecalhosValidadores"
        :items="validadoresFiltrados"
        :loading="blockchainStore.isLoadingValidators"
        loading-text="Carregando validadores..."
        no-data-text="Nenhum validador encontrado"
        hover
        item-value="address"
      >
        <!-- Status -->
        <template v-slot:item.status="{ item }">
          <v-chip
            :color="item.status === 'active' ? 'success' : 'grey'"
            size="small"
            label
          >
            {{ item.status === 'active' ? 'Ativo' : 'Inativo' }}
          </v-chip>
        </template>
        
        <!-- Nome / Endereço -->
        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center">
            <v-avatar
              :color="getValidatorColor(item.merit)"
              size="32"
              class="mr-2 text-white"
            >
              {{ item.name.charAt(0) }}
            </v-avatar>
            
            <div>
              <router-link 
                :to="{ name: 'validator-details', params: { id: item.address } }"
                class="text-decoration-none font-weight-medium"
              >
                {{ item.name }}
              </router-link>
              <div class="text-caption text-truncate" style="max-width: 180px;">
                {{ formatarHash(item.address) }}
              </div>
            </div>
          </div>
        </template>
        
        <!-- Stake -->
        <template v-slot:item.stake="{ item }">
          <div class="font-weight-medium">
            {{ formatarValorToken(item.stake) }}
          </div>
        </template>
        
        <!-- Mérito -->
        <template v-slot:item.merit="{ item }">
          <v-tooltip location="top">
            <template v-slot:activator="{ props }">
              <v-progress-linear
                v-bind="props"
                model-value="item.merit"
                :color="getValidatorColor(item.merit)"
                height="8"
                rounded
              ></v-progress-linear>
            </template>
            {{ item.merit }}/100
          </v-tooltip>
          <div class="text-caption text-center mt-1">
            {{ item.merit }}/100
          </div>
        </template>
        
        <!-- Esquema Cripto -->
        <template v-slot:item.signatureScheme="{ item }">
          <v-chip
            :color="item.signatureScheme === 'Dilithium3' ? 'purple' : 'blue-grey'"
            size="small"
            label
          >
            {{ item.signatureScheme }}
          </v-chip>
        </template>
        
        <!-- Pontuação PoSEM -->
        <template v-slot:item.posemScore="{ item }">
          <v-tooltip location="top">
            <template v-slot:activator="{ props }">
              <v-progress-linear
                v-bind="props"
                model-value="item.posemScore"
                color="primary"
                height="8"
                rounded
              ></v-progress-linear>
            </template>
            {{ item.posemScore }}/100
          </v-tooltip>
          <div class="text-caption text-center mt-1">
            {{ item.posemScore }}/100
          </div>
        </template>
        
        <!-- Probabilidade -->
        <template v-slot:item.probability="{ item }">
          <div class="text-caption">
            {{ ((item.stake * (1 + 0.1 * item.merit)) / getTotalWeightedStake() * 100).toFixed(1) }}%
          </div>
        </template>
        
        <!-- Blocos Propostos -->
        <template v-slot:item.blocksProposed="{ item }">
          <div class="text-caption">
            {{ formatarNumeroGrande(item.blocksProposed) }}
          </div>
        </template>
        
        <!-- Uptime -->
        <template v-slot:item.uptime="{ item }">
          <v-tooltip location="top">
            <template v-slot:activator="{ props }">
              <v-progress-linear
                v-bind="props"
                model-value="item.uptime"
                :color="item.uptime > 95 ? 'success' : (item.uptime > 90 ? 'warning' : 'error')"
                height="8"
                rounded
              ></v-progress-linear>
            </template>
            {{ item.uptime.toFixed(2) }}%
          </v-tooltip>
          <div class="text-caption text-center mt-1">
            {{ item.uptime.toFixed(2) }}%
          </div>
        </template>
        
        <!-- Última Atividade -->
        <template v-slot:item.lastActive="{ item }">
          <div class="text-caption">
            {{ tempoRelativo(item.lastActive) }}
          </div>
        </template>
        
        <!-- Ações -->
        <template v-slot:item.actions="{ item }">
          <v-btn
            icon
            variant="text"
            density="comfortable"
            size="small"
            :to="{ name: 'validator-details', params: { id: item.address } }"
          >
            <v-icon>mdi-information-outline</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
    
    <!-- Diálogo de Visualização 3D com componente real -->
    <v-dialog v-model="dialogVisualizacao3D" fullscreen>
      <v-card>
        <v-toolbar color="primary" dark>
          <v-btn icon @click="dialogVisualizacao3D = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ t('validators.posem3d') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="simularAnimacao">
            <v-icon>mdi-play</v-icon>
          </v-btn>
        </v-toolbar>
        
        <v-card-text class="pa-0">
          <PoSEM3DVisualization 
            :validators="blockchainStore.validators" 
            ref="visualizacao3D"
            @simulation-complete="onSimulationComplete"
            @leader-selected="onLeaderSelected"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useBlockchainStore } from '@/stores/blockchain'
import { formatarHash, formatarValorToken, formatarNumeroGrande, tempoRelativo } from '@/services/utilidades'
import StatusCard from '@/components/common/StatusCard.vue'
import PoSEM3DVisualization from '@/components/validators/PoSEM3DVisualization.vue'

export default {
  name: 'ValidatorsView',
  components: {
    StatusCard,
    PoSEM3DVisualization
  },
  data() {
    return {
      busca: '',
      buscaDebounced: '',
      _searchTimer: null,
      filtroCountry: null,
      filtroRegion: null,
      modoAgrupado: false,
      showValidatorInfo: null,
      dialogVisualizacao3D: false,
      cabecalhosValidadores: [
        { title: 'Status', key: 'status', align: 'center', width: '100px' },
        { title: 'Validador', key: 'name', align: 'start' },
        { title: 'Stake', key: 'stake', align: 'end' },
        { title: 'Mérito', key: 'merit', align: 'center' },
        { title: 'Esquema Cripto', key: 'signatureScheme', align: 'center' },
        { title: 'PoSEM', key: 'posemScore', align: 'center' },
        { title: 'Probabilidade', key: 'probability', align: 'center' },
        { title: 'Blocos', key: 'blocksProposed', align: 'center' },
        { title: 'Uptime', key: 'uptime', align: 'center' },
        { title: 'Última Atividade', key: 'lastActive', align: 'start' },
        { title: '', key: 'actions', align: 'center', sortable: false, width: '50px' },
      ]
    }
  },
  computed: {
    blockchainStore() {
      return useBlockchainStore()
    },
    
    paisesDisponiveis() {
      const countries = [...new Set(
        this.blockchainStore.validators.map(v => v.country).filter(Boolean)
      )]
      return countries.sort()
    },
    
    regioesDisponiveis() {
      if (!this.filtroCountry) return []
      const regions = [...new Set(
        this.blockchainStore.validators
          .filter(v => v.country === this.filtroCountry)
          .map(v => v.region)
          .filter(Boolean)
      )]
      return regions.sort()
    },
    
    validadoresFiltrados() {
      let resultado = this.blockchainStore.validators
      if (this.filtroCountry) {
        resultado = resultado.filter(v => v.country === this.filtroCountry)
      }
      if (this.filtroRegion) {
        resultado = resultado.filter(v => v.region === this.filtroRegion)
      }
      if (this.buscaDebounced) {
        const termo = this.buscaDebounced.toLowerCase()
        resultado = resultado.filter(v =>
          v.name.toLowerCase().includes(termo) ||
          v.address.toLowerCase().includes(termo) ||
          (v.city || '').toLowerCase().includes(termo)
        )
      }
      return resultado
    },
    
    validadoresAgrupados() {
      const groups = {}
      for (const v of this.validadoresFiltrados) {
        const key = v.country || 'Desconhecido'
        if (!groups[key]) groups[key] = []
        groups[key].push(v)
      }
      return Object.entries(groups)
        .sort((a, b) => b[1].length - a[1].length)
        .map(([country, validators]) => ({ country, validators, count: validators.length }))
    }
  },
  methods: {
    formatarHash,
    formatarValorToken,
    formatarNumeroGrande,
    tempoRelativo,
    
    calcularMeritoMedio() {
      if (this.blockchainStore.validators.length === 0) return 0
      
      const total = this.blockchainStore.validators.reduce((sum, validator) => sum + validator.merit, 0)
      return Math.round(total / this.blockchainStore.validators.length)
    },
    
    calcularUptimeMedio() {
      if (this.blockchainStore.validators.length === 0) return 0
      
      const total = this.blockchainStore.validators.reduce((sum, validator) => sum + validator.uptime, 0)
      return total / this.blockchainStore.validators.length
    },
    
    getValidatorColor(merit) {
      // Gera cor baseada no mérito (vermelho para baixo, verde para alto)
      if (merit >= 90) return '#4CAF50' // Verde
      if (merit >= 70) return '#8BC34A' // Verde Claro
      if (merit >= 50) return '#FFC107' // Amarelo
      if (merit >= 30) return '#FF9800' // Laranja
      return '#F44336' // Vermelho
    },
    
    getTotalWeightedStake() {
      return this.blockchainStore.validators.reduce((sum, validator) => {
        return sum + validator.stake * (1 + 0.1 * validator.merit)
      }, 0)
    },
    
    abrirVisualizacao3D() {
      this.dialogVisualizacao3D = true
    },
    
    resetarVisualizacao() {
      // Em produção, resetaria a visualização 3D
      console.log('Visualização 3D resetada')
    },
    
    simularAnimacao() {
      // Inicia a simulação de seleção de líder usando o componente real
      if (this.$refs.visualizacao3D) {
        this.$refs.visualizacao3D.startSimulation()
      }
    },
    
    onSimulationComplete() {
      // Quando a simulação é concluída
      this.blockchainStore.fetchValidators() // Atualiza dados dos validadores
    },
    
    onLeaderSelected(leader) {
      // Quando um líder é selecionado na simulação
      console.log('Líder selecionado:', leader.name)
      
      // Notifica o usuário sobre o líder selecionado
      this.$nextTick(() => {
        // Usa setTimeout para garantir que o Vue tenha tempo de atualizar a UI
        setTimeout(() => {
          const toast = this.$root.$el.querySelector('.v-snackbar')
          if (toast) toast.__vue__.close()
        }, 100)
      })
    }
  },
  async mounted() {
    if (this.blockchainStore.validators.length === 0) {
      await this.blockchainStore.fetchValidators()
    }
  },
  watch: {
    busca(newVal) {
      clearTimeout(this._searchTimer)
      this._searchTimer = setTimeout(() => {
        this.buscaDebounced = newVal
      }, 300)
    },
    filtroCountry() {
      this.filtroRegion = null
    }
  }
}
</script>

<style scoped>
.validators-view {
  padding-bottom: 32px;
}

.posem-visualization {
  position: relative;
  width: 100%;
  height: 300px;
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  overflow: hidden;
}

.validator-node {
  position: absolute;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  z-index: 1;
}

.validator-node:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.5);
  z-index: 10;
}

.validator-initial {
  font-size: 16px;
  text-transform: uppercase;
}

.validator-tooltip {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(33, 33, 33, 0.9);
  padding: 8px 12px;
  border-radius: 4px;
  width: 180px;
  z-index: 100;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}
</style>
